import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from 'services/api';
import useAuthStore from 'stores/authStore';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from 'components/ui/card';
import { Checkbox } from 'components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Textarea } from 'components/ui/textarea';
import { Heart, ArrowRight, ArrowLeft, User } from 'lucide-react';
import { toast } from 'sonner';

const CONDITIONS = [
  "Alzheimer's/Dementia",
  "Parkinson's Disease",
  "Diabetes",
  "Heart Disease",
  "Arthritis",
  "COPD/Respiratory",
  "Stroke Recovery",
  "Cancer",
  "Vision Impairment",
  "Hearing Impairment",
  "Other"
];

const CONCERNS = [
  "Fall Risk",
  "Medication Management",
  "Bathing/Personal Care",
  "Meal Preparation",
  "Wandering/Safety",
  "Transportation",
  "Companionship",
  "Night Supervision",
  "Memory Issues",
  "Mobility Assistance"
];

export default function CareProfileSetup() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    care_recipient_name: '',
    age: '',
    conditions: [],
    mobility_level: '',
    cognitive_status: '',
    living_arrangement: '',
    primary_concerns: [],
    notes: ''
  });

  const [existingProfileId, setExistingProfileId] = useState(null);

  useEffect(() => {
    api.get('/care-profiles').then(response => {
      const data = response.data;
      const profiles = Array.isArray(data) ? data : data?.profiles || [];
      const p = profiles.find(pr => pr.user_id === user?.user_id);
      if (p) {
        setExistingProfileId(p.profile_id);
        setFormData({
          care_recipient_name: p.care_recipient_name || '',
          age: p.age != null ? String(p.age) : '',
          conditions: p.conditions || [],
          mobility_level: p.mobility_level || '',
          cognitive_status: p.cognitive_status || '',
          living_arrangement: p.living_arrangement || '',
          primary_concerns: p.primary_concerns || [],
          notes: p.notes || ''
        });
      }
    }).catch(err => console.error('Failed to load profile:', err));
  }, []);

  const handleConditionToggle = (condition) => {
    setFormData(prev => ({
      ...prev,
      conditions: prev.conditions.includes(condition)
        ? prev.conditions.filter(c => c !== condition)
        : [...prev.conditions, condition]
    }));
  };

  const handleConcernToggle = (concern) => {
    setFormData(prev => ({
      ...prev,
      primary_concerns: prev.primary_concerns.includes(concern)
        ? prev.primary_concerns.filter(c => c !== concern)
        : [...prev.primary_concerns, concern]
    }));
  };

  const handleSubmit = async () => {
    if (!formData.age || !formData.mobility_level || !formData.cognitive_status || !formData.living_arrangement) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      if (existingProfileId) {
        await api.put(`/care-profiles/${existingProfileId}`, {
          ...formData,
          age: parseInt(formData.age)
        });
      } else {
        await api.post('/care-profiles', {
          ...formData,
          age: parseInt(formData.age)
        });
      }

      toast.success(existingProfileId ? 'Care profile updated!' : 'Care profile created!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-xl bg-teal-500 flex items-center justify-center">
              <Heart className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-900 font-['Manrope']">ElderLink</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Care Profile Setup</h1>
          <p className="text-slate-500 mt-2">Tell us about your loved one to get personalized guidance</p>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className={`w-24 h-1 rounded-full ${step >= s ? 'bg-teal-500' : 'bg-slate-200'}`} />
          ))}
        </div>

        <Card className="border-0 shadow-xl" data-testid="care-profile-setup">
          <CardContent className="p-4 sm:p-8">
            {/* Step 1: Basic Info */}
            {step === 1 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-4">
                    <User className="w-8 h-8 text-teal-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-slate-900 font-['Manrope']">Basic Information</h2>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Care Recipient's Name (Optional)</Label>
                    <Input
                      id="name"
                      placeholder="e.g., Mom, Dad, Grandma..."
                      value={formData.care_recipient_name}
                      onChange={(e) => setFormData({ ...formData, care_recipient_name: e.target.value })}
                      className="h-12"
                      data-testid="recipient-name-input"
                    />
                    <p className="text-xs text-slate-500">This is just for your reference</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="age">Age *</Label>
                    <Input
                      id="age"
                      type="number"
                      placeholder="e.g., 78"
                      value={formData.age}
                      onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                      className="h-12"
                      required
                      data-testid="age-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Living Arrangement *</Label>
                    <Select
                      value={formData.living_arrangement}
                      onValueChange={(value) => setFormData({ ...formData, living_arrangement: value })}
                    >
                      <SelectTrigger className="h-12" data-testid="living-arrangement-select">
                        <SelectValue placeholder="Select living arrangement" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="own_home">Living in own home</SelectItem>
                        <SelectItem value="with_family">Living with family</SelectItem>
                        <SelectItem value="assisted_living">Assisted living facility</SelectItem>
                        <SelectItem value="nursing_home">Nursing home</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button
                  onClick={() => setStep(2)}
                  className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white rounded-lg"
                  disabled={!formData.age || !formData.living_arrangement}
                  data-testid="next-step-1-btn"
                >
                  Continue
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </div>
            )}

            {/* Step 2: Health Status */}
            {step === 2 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <h2 className="text-xl font-semibold text-slate-900 font-['Manrope']">Health Status</h2>
                  <p className="text-slate-500 text-sm">This helps us personalize AI guidance</p>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Mobility Level *</Label>
                    <Select
                      value={formData.mobility_level}
                      onValueChange={(value) => setFormData({ ...formData, mobility_level: value })}
                    >
                      <SelectTrigger className="h-12" data-testid="mobility-select">
                        <SelectValue placeholder="Select mobility level" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fully_mobile">Fully Mobile</SelectItem>
                        <SelectItem value="mobile_with_aids">Mobile with Aids (cane, walker)</SelectItem>
                        <SelectItem value="limited_mobility">Limited Mobility</SelectItem>
                        <SelectItem value="wheelchair">Wheelchair User</SelectItem>
                        <SelectItem value="bedridden">Bedridden</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Cognitive Status *</Label>
                    <Select
                      value={formData.cognitive_status}
                      onValueChange={(value) => setFormData({ ...formData, cognitive_status: value })}
                    >
                      <SelectTrigger className="h-12" data-testid="cognitive-select">
                        <SelectValue placeholder="Select cognitive status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="no_impairment">No Cognitive Impairment</SelectItem>
                        <SelectItem value="mild_impairment">Mild Cognitive Impairment</SelectItem>
                        <SelectItem value="moderate_dementia">Moderate Dementia</SelectItem>
                        <SelectItem value="severe_dementia">Severe Dementia</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-3">
                    <Label>Health Conditions (select all that apply)</Label>
                    <div className="grid grid-cols-2 gap-1.5 sm:gap-2">
                      {CONDITIONS.map((condition) => (
                        <label
                          key={condition}
                          className={`flex items-center gap-1.5 sm:gap-2 px-2 py-1.5 sm:p-2.5 rounded-md border cursor-pointer transition-colors min-w-0 ${
                            formData.conditions.includes(condition)
                              ? 'border-teal-500 bg-teal-50'
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <Checkbox
                            checked={formData.conditions.includes(condition)}
                            onCheckedChange={() => handleConditionToggle(condition)}
                            className="shrink-0"
                          />
                          <span className="text-xs sm:text-sm leading-tight">{condition}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setStep(1)}
                    className="flex-1 h-12 rounded-lg"
                  >
                    <ArrowLeft className="w-5 h-5" />
                    Back
                  </Button>
                  <Button
                    onClick={() => setStep(3)}
                    className="flex-1 h-12 bg-slate-900 hover:bg-slate-800 text-white rounded-lg"
                    disabled={!formData.mobility_level || !formData.cognitive_status}
                    data-testid="next-step-2-btn"
                  >
                    Continue
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Concerns & Notes */}
            {step === 3 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <h2 className="text-xl font-semibold text-slate-900 font-['Manrope']">Care Needs</h2>
                  <p className="text-slate-500 text-sm">What areas need the most attention?</p>
                </div>

                <div className="space-y-4">
                  <div className="space-y-3">
                    <Label>Primary Concerns (select all that apply)</Label>
                    <div className="grid grid-cols-2 gap-1.5 sm:gap-2">
                      {CONCERNS.map((concern) => (
                        <label
                          key={concern}
                          className={`flex items-center gap-1.5 sm:gap-2 px-2 py-1.5 sm:p-2.5 rounded-md border cursor-pointer transition-colors min-w-0 ${
                            formData.primary_concerns.includes(concern)
                              ? 'border-teal-500 bg-teal-50'
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <Checkbox
                            checked={formData.primary_concerns.includes(concern)}
                            onCheckedChange={() => handleConcernToggle(concern)}
                            className="shrink-0"
                          />
                          <span className="text-xs sm:text-sm leading-tight">{concern}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="notes">Additional Notes (Optional)</Label>
                    <Textarea
                      id="notes"
                      placeholder="Any other important information about your loved one's care needs..."
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      className="min-h-[100px]"
                      data-testid="notes-textarea"
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setStep(2)}
                    className="flex-1 h-12 rounded-lg"
                  >
                    <ArrowLeft className="w-5 h-5" />
                    Back
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    className="flex-1 h-12 bg-teal-500 hover:bg-teal-600 text-white rounded-lg"
                    disabled={loading}
                    data-testid="complete-setup-btn"
                  >
                    {loading ? 'Creating...' : 'Complete Setup'}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-sm text-slate-500 mt-6">
          You can always update this information later in Settings
        </p>
      </div>
    </div>
  );
}
